import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  AhtFoundayoReviewPanel,
  type FoundayoPanelState,
  type FoundayoPanelValues,
} from "./AhtFoundayoReviewPanel";
import { reviewRecords } from "@/features/aht-weight/reviewRecordStore";
import type { PlanReviewContext } from "@/features/aht-weight/planReviewContext";

export interface AHTFollowUpReviewFormData {
  batchNo: string;
  brandName: string;
  missedDoses: string;
  missedDosesDetail: string;
  conditionChanges: string;
  conditionDetail: string;
  medChanges: string;
  medDetail: string;
  adverseEffects: string;
  adverseDetail: string;
  generalWellbeing: string;
  weightKg: string;
  bmi: string;
  weightChangeSinceLastReview: string;
  lifestyleNotes: string;
  complianceConcerns: string;
  complianceDetail: string;
  photoFullLength: string;
  photoFullLengthNo: string;
  photoScale: string;
  photoScaleNo: string;
  weightPlausible: string;
  weightPlausibleDetail: string;
  selectedDrug: string;
  injectionNotes: string;
  injectionTime: string;
  titrationStep: string;
  efficacy12wk: string;
  liraBrand: string;
  liraBatch: string;
  mysimbaStep: string;
  bpSystolic: string;
  bpDiastolic: string;
  pulse: string;
  fatAdherenceNotes: string;
  orlistat12wk: string;
  redFlagTriggered: string;
  redFlagDetail: string;
  bmiFloor: string;
  bmiFloorAction: string;
  decision: string;
  outcomeMatch: string;
  medicinePrescribed: string;
  quantity: string;
  supplyBatch: string;
  supplyExpiry: string;
  gpNotified: string;
  nextReviewDate: string;
  clinicalNotes: string;
  sigName: string;
  sigRegNo: string;
  sigText: string;
  sigDate: string;
  /** Foundayo (orforglipron) monthly review — only present when Foundayo is the reviewed drug. */
  foundayo?: FoundayoPanelValues;
  foundayoStopReasons?: string[];
  foundayoReferrals?: string[];
  foundayoNeverRestart?: boolean;
  foundayoPercentChangeFromInitial?: number | null;
  maxToleratedDoseDate?: string | null;
}

interface AHTFollowUpReviewTemplateProps {
  isDarkMode: boolean;
  patientName?: string;
  patientDob?: string;
  drug?: string;
  currentDose?: string;
  treatmentWeek?: number;
  cycleNumber?: number;
  presciberName?: string;
  presciberRegNumber?: string;
  reviewDate?: string;
  missedDoses?: string;
  conditionChanges?: string;
  conditionChangesDetail?: string;
  medicationChanges?: string;
  medicationChangesDetail?: string;
  adverseEffects?: string;
  wellbeing?: string;
  currentWeightKg?: string;
  dosePreference?: string;
  complianceConcerns?: string;
  complianceConcernsDetail?: string;
  onComplete?: (data: AHTFollowUpReviewFormData) => void;
  loading?: boolean;
  /** Treatment plan this review belongs to. Needed to link the saved review to the plan. */
  planContext?: PlanReviewContext;
}

function detectDrugKey(drug?: string): string {
  if (!drug) return "";
  const d = drug.toLowerCase();
  if (d.includes("tirzepatide") || d.includes("mounjaro")) return "tirzepatide";
  if (d.includes("semaglutide") || d.includes("wegovy")) return "semaglutide";
  if (d.includes("liraglutide") || d.includes("saxenda") || d.includes("biolide")) return "liraglutide";
  if (d.includes("mysimba") || d.includes("naltrexone") || d.includes("bupropion")) return "mysimba";
  if (d.includes("orlistat") || d.includes("xenical")) return "orlistat";
  if (d.includes("foundayo") || d.includes("orforglipron")) return "foundayo";
  return "";
}

function SectionHeader({
  num,
  title,
  open,
  isDarkMode,
}: {
  num: number;
  title: string;
  open: boolean;
  isDarkMode: boolean;
}) {
  return (
    <CollapsibleTrigger className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left font-semibold text-sm transition-colors ${
      isDarkMode
        ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
        : "bg-gray-100 hover:bg-gray-200 text-gray-900"
    }`}>
      <span>{num}. {title}</span>
      {open ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
    </CollapsibleTrigger>
  );
}

function FieldLabel({ label, required, isDarkMode }: { label: string; required?: boolean; isDarkMode: boolean }) {
  return (
    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

function TextInput({ value, onChange, placeholder, isDarkMode, className = "" }: {
  value: string; onChange: (v: string) => void; placeholder?: string; isDarkMode: boolean; className?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-3 py-2 text-sm rounded-lg border ${
        isDarkMode
          ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
      } ${className}`}
    />
  );
}

function ReadOnlyField({ value, isDarkMode }: { value: string; isDarkMode: boolean }) {
  return (
    <div className={`px-3 py-2 text-sm rounded-lg border ${
      isDarkMode ? "bg-gray-800 border-gray-600 text-gray-300" : "bg-gray-50 border-gray-200 text-gray-700"
    }`}>
      {value || <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>—</span>}
    </div>
  );
}

function YesNoRow({
  label,
  value,
  onChange,
  condLabel,
  condValue,
  onCondChange,
  isDarkMode,
  redBorder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  condLabel?: string;
  condValue?: string;
  onCondChange?: (v: string) => void;
  isDarkMode: boolean;
  redBorder?: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-4">
        <span className={`text-sm flex-1 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{label}</span>
        <div className="flex gap-3 shrink-0">
          {["Yes", "No"].map(opt => (
            <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name={label}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
                className="accent-blue-600"
              />
              <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
            </label>
          ))}
        </div>
      </div>
      {value === "Yes" && condLabel && onCondChange && (
        <div className="ml-4">
          <FieldLabel label={condLabel} isDarkMode={isDarkMode} />
          <TextInput
            value={condValue ?? ""}
            onChange={onCondChange}
            isDarkMode={isDarkMode}
            className={redBorder ? "border-red-500" : ""}
          />
        </div>
      )}
    </div>
  );
}

export function AHTFollowUpReviewTemplate({
  isDarkMode,
  patientName,
  patientDob,
  drug,
  currentDose,
  treatmentWeek,
  cycleNumber,
  presciberName,
  presciberRegNumber,
  reviewDate,
  missedDoses: missedDosesProp,
  conditionChanges: conditionChangesProp,
  conditionChangesDetail: conditionChangesDetailProp,
  medicationChanges: medicationChangesProp,
  medicationChangesDetail: medicationChangesDetailProp,
  adverseEffects: adverseEffectsProp,
  wellbeing: wellbeingProp,
  currentWeightKg: currentWeightKgProp,
  dosePreference: dosePreferenceProp,
  complianceConcerns: complianceConcernsProp,
  complianceConcernsDetail: complianceConcernsDetailProp,
  onComplete,
  loading,
  planContext,
}: AHTFollowUpReviewTemplateProps) {
  const today = new Date().toISOString().split("T")[0]!;
  const detectedDrug = detectDrugKey(drug);
  const isLiraglutide = detectedDrug === "liraglutide";

  const [open, setOpen] = useState<Record<number, boolean>>({
    1: true, 2: true, 3: true, 4: true, 5: true, 6: true, 7: true, 8: true,
  });
  const toggle = (n: number) => setOpen(p => ({ ...p, [n]: !p[n] }));

  const [batchNo, setBatchNo] = useState("");
  const [brandName, setBrandName] = useState("");

  const [missedDoses, setMissedDoses] = useState(
    missedDosesProp && missedDosesProp !== "No missed doses" ? "Yes" : (missedDosesProp === "No missed doses" ? "No" : "")
  );
  const [missedDosesDetail, setMissedDosesDetail] = useState("");
  const [conditionChanges, setConditionChanges] = useState(
    conditionChangesProp && conditionChangesProp !== "No changes" ? "Yes" : (conditionChangesProp === "No changes" ? "No" : "")
  );
  const [conditionDetail, setConditionDetail] = useState(conditionChangesDetailProp ?? "");
  const [medChanges, setMedChanges] = useState(
    medicationChangesProp && medicationChangesProp !== "No changes" ? "Yes" : (medicationChangesProp === "No changes" ? "No" : "")
  );
  const [medDetail, setMedDetail] = useState(medicationChangesDetailProp ?? "");
  const [adverseEffects, setAdverseEffects] = useState(adverseEffectsProp ?? "");
  const [adverseDetail, setAdverseDetail] = useState("");
  const [generalWellbeing, setGeneralWellbeing] = useState(wellbeingProp ?? "");
  const [weightKg, setWeightKg] = useState(currentWeightKgProp ?? "");
  const [bmi, setBmi] = useState("");
  const [weightChangeSinceLastReview, setWeightChangeSinceLastReview] = useState("");
  const [lifestyleNotes, setLifestyleNotes] = useState("");
  const [complianceConcerns, setComplianceConcerns] = useState(
    complianceConcernsProp && complianceConcernsProp !== "No concerns" ? "Yes" : (complianceConcernsProp === "No concerns" ? "No" : "")
  );
  const [complianceDetail, setComplianceDetail] = useState(complianceConcernsDetailProp ?? "");

  const [photoFullLength, setPhotoFullLength] = useState("");
  const [photoScale, setPhotoScale] = useState("");
  const [weightPlausible, setWeightPlausible] = useState("");
  const [weightPlausibleDetail, setWeightPlausibleDetail] = useState("");
  const [photoFullLengthNo, setPhotoFullLengthNo] = useState("");
  const [photoScaleNo, setPhotoScaleNo] = useState("");

  const [selectedDrug, setSelectedDrug] = useState(detectedDrug);
  const [injectionNotes, setInjectionNotes] = useState("");
  const [injectionTime, setInjectionTime] = useState("");
  const [titrationStep, setTitrationStep] = useState("");
  const [efficacy12wk, setEfficacy12wk] = useState("");
  const [liraBrand, setLiraBrand] = useState("Saxenda®");
  const [liraBatch, setLiraBatch] = useState("");
  const [mysimbaStep, setMysimbaStep] = useState("");
  const [bpSystolic, setBpSystolic] = useState("");
  const [bpDiastolic, setBpDiastolic] = useState("");
  const [pulse, setPulse] = useState("");
  const [fatAdherenceNotes, setFatAdherenceNotes] = useState("");
  const [orlistat12wk, setOrlistat12wk] = useState("");

  const [redFlagTriggered, setRedFlagTriggered] = useState("");
  const [redFlagDetail, setRedFlagDetail] = useState("");
  const [bmiFloor, setBmiFloor] = useState("");
  const [bmiFloorAction, setBmiFloorAction] = useState("");

  const [decision, setDecision] = useState(
    dosePreferenceProp === "Escalate to next dose"
      ? "Escalate to next dose"
      : dosePreferenceProp === "Continue at current dose"
        ? "Continue same dose"
        : ""
  );
  const [outcomeMatch, setOutcomeMatch] = useState("");
  const [medicinePrescribed, setMedicinePrescribed] = useState("");
  const [quantity, setQuantity] = useState("");
  const [supplyBatch, setSupplyBatch] = useState("");
  const [supplyExpiry, setSupplyExpiry] = useState("");
  const [gpNotified, setGpNotified] = useState("");
  const [nextReviewDate, setNextReviewDate] = useState("");

  const [clinicalNotes, setClinicalNotes] = useState("");

  const [foundayo, setFoundayo] = useState<FoundayoPanelState | null>(null);

  const [sigName, setSigName] = useState(presciberName ?? "");
  const [sigRegNo, setSigRegNo] = useState(presciberRegNumber ?? "");
  const [sigText, setSigText] = useState("");
  const [sigDate, setSigDate] = useState(today);

  const dm = isDarkMode;
  const cardClass = `rounded-lg border ${dm ? "border-gray-700" : "border-gray-200"}`;
  const inputClass = `w-full px-3 py-2 text-sm rounded-lg border ${dm ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`;
  const labelClass = `block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-700"}`;
  const sectionBodyClass = `p-4 space-y-4 ${dm ? "bg-gray-800" : "bg-white"}`;

  const drugs = [
    { key: "tirzepatide", label: "Tirzepatide (Mounjaro®)" },
    { key: "semaglutide", label: "Semaglutide (Wegovy®)" },
    { key: "liraglutide", label: "Liraglutide (Saxenda®/Biolide®)" },
    { key: "mysimba", label: "Naltrexone/bupropion (Mysimba®)" },
    { key: "orlistat", label: "Orlistat (Xenical®)" },
    { key: "foundayo", label: "Orforglipron (Foundayo®)" },
  ];

  const isFoundayo = selectedDrug === "foundayo";
  // Foundayo decides from its own panel; every other drug uses the Section 6 decision.
  const effectiveDecision = isFoundayo ? (foundayo?.values.doseDecision ?? "") : decision;
  const foundayoBlockers = isFoundayo
    ? foundayo
      ? foundayo.saveBlockers
      : ["Complete the Foundayo review section."]
    : [];

  const handleSubmit = () => {
    if (!onComplete) return;
    if (isFoundayo && foundayo && planContext) {
      // Append-only audit record linked to the plan and its latest supply event. It is also what the
      // never-restart lookup reads, so a confirmed-pancreatitis review blocks Foundayo at selection.
      reviewRecords.append({
        planId: planContext.planId,
        supplyEventId: planContext.supplyEventId,
        patientId: planContext.patientId,
        kind: "4week",
        drugId: "foundayo",
        data: {
          reviewDate: reviewDate ?? today,
          treatmentWeek,
          cycleNumber,
          currentDose,
          foundayo: foundayo.values,
          foundayoStopReasons: foundayo.result.stopReasons,
          foundayoReferrals: foundayo.result.referrals,
          foundayoNeverRestart: foundayo.result.neverRestart,
          foundayoPercentChangeFromInitial: foundayo.result.percentChangeFromInitial,
          waistDate: foundayo.values.waistCm ? today : undefined,
          maxToleratedDoseDate: foundayo.maxToleratedDoseDate ?? undefined,
          clinicalNotes,
          sigName,
          sigRegNo,
          sigText,
          sigDate,
        },
      });
    }
    onComplete({
      batchNo,
      brandName,
      missedDoses,
      missedDosesDetail,
      conditionChanges,
      conditionDetail,
      medChanges,
      medDetail,
      adverseEffects,
      adverseDetail,
      generalWellbeing,
      weightKg,
      bmi,
      weightChangeSinceLastReview,
      lifestyleNotes,
      complianceConcerns,
      complianceDetail,
      photoFullLength,
      photoFullLengthNo,
      photoScale,
      photoScaleNo,
      weightPlausible,
      weightPlausibleDetail,
      selectedDrug,
      injectionNotes,
      injectionTime,
      titrationStep,
      efficacy12wk,
      liraBrand,
      liraBatch,
      mysimbaStep,
      bpSystolic,
      bpDiastolic,
      pulse,
      fatAdherenceNotes,
      orlistat12wk,
      redFlagTriggered,
      redFlagDetail,
      bmiFloor,
      bmiFloorAction,
      decision: effectiveDecision,
      outcomeMatch,
      medicinePrescribed,
      quantity,
      supplyBatch,
      supplyExpiry,
      gpNotified,
      nextReviewDate,
      clinicalNotes,
      sigName,
      sigRegNo,
      sigText,
      sigDate,
      ...(isFoundayo && foundayo
        ? {
            foundayo: foundayo.values,
            foundayoStopReasons: foundayo.result.stopReasons,
            foundayoReferrals: foundayo.result.referrals,
            foundayoNeverRestart: foundayo.result.neverRestart,
            foundayoPercentChangeFromInitial: foundayo.result.percentChangeFromInitial,
            maxToleratedDoseDate: foundayo.maxToleratedDoseDate,
          }
        : {}),
    });
  };

  return (
    <div className={`space-y-3 text-sm ${dm ? "text-gray-200" : "text-gray-900"}`}>

      {/* Title */}
      <div className={`px-4 py-3 rounded-lg border ${dm ? "bg-purple-900/20 border-purple-700" : "bg-purple-50 border-purple-200"}`}>
        <p className={`font-semibold ${dm ? "text-purple-200" : "text-purple-900"}`}>
          AHT Weight Management — 4-Week Supply Review
        </p>
        <p className={`text-xs mt-0.5 ${dm ? "text-purple-300" : "text-purple-700"}`}>
          Allied Health Training Limited · AHT-WM-4WK-v1
        </p>
      </div>

      {/* Section 1 — Patient & supply details */}
      <Collapsible open={open[1]} onOpenChange={() => toggle(1)} className={cardClass}>
        <SectionHeader num={1} title="Patient & supply details" open={!!open[1]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Patient name</label>
                <ReadOnlyField value={patientName ?? ""} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Date of birth</label>
                <ReadOnlyField value={patientDob ?? ""} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Medicine & current dose</label>
                <ReadOnlyField value={[drug, currentDose].filter(Boolean).join(" — ")} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Treatment week / cycle</label>
                <ReadOnlyField value={[treatmentWeek ? `Week ${treatmentWeek}` : "", cycleNumber ? `Cycle ${cycleNumber}` : ""].filter(Boolean).join(" · ")} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Date of review</label>
                <ReadOnlyField value={reviewDate ?? today} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Prescriber name & reg. no.</label>
                <ReadOnlyField value={[presciberName, presciberRegNumber].filter(Boolean).join(" · ")} isDarkMode={dm} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>
                  Brand name{isLiraglutide && <span className="text-amber-500 ml-1">(mandatory — biological product)</span>}
                </label>
                <input
                  type="text"
                  value={brandName}
                  onChange={e => setBrandName(e.target.value)}
                  placeholder={isLiraglutide ? "Saxenda® or Biolide®" : "Brand name"}
                  className={`${inputClass}${isLiraglutide && !brandName ? " border-amber-400" : ""}`}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Batch no.{isLiraglutide && <span className="text-amber-500 ml-1">(mandatory)</span>}
                </label>
                <input
                  type="text"
                  value={batchNo}
                  onChange={e => setBatchNo(e.target.value)}
                  placeholder="e.g. BN-2024-001"
                  className={`${inputClass}${isLiraglutide && !batchNo ? " border-amber-400" : ""}`}
                />
              </div>
            </div>
            {isLiraglutide && (
              <p className={`text-xs px-3 py-2 rounded border ${dm ? "bg-amber-900/20 border-amber-700 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                Liraglutide is a biological product. Brand name and batch number must be recorded at every supply.
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 2 — Questionnaire review */}
      <Collapsible open={open[2]} onOpenChange={() => toggle(2)} className={cardClass}>
        <SectionHeader num={2} title="Questionnaire review" open={!!open[2]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <YesNoRow label="Missed doses since last supply?" value={missedDoses} onChange={setMissedDoses} condLabel="Details:" condValue={missedDosesDetail} onCondChange={setMissedDosesDetail} isDarkMode={dm} />
            <YesNoRow label="Changes to medical conditions since initiation?" value={conditionChanges} onChange={setConditionChanges} condLabel="Details:" condValue={conditionDetail} onCondChange={setConditionDetail} isDarkMode={dm} />
            <YesNoRow label="Changes to medication / OTC since initiation?" value={medChanges} onChange={setMedChanges} condLabel="Details:" condValue={medDetail} onCondChange={setMedDetail} isDarkMode={dm} />
            <YesNoRow label="Adverse effects? (and how managed)" value={adverseEffects} onChange={setAdverseEffects} condLabel="Effect and management:" condValue={adverseDetail} onCondChange={setAdverseDetail} isDarkMode={dm} />

            <div>
              <label className={labelClass}>How they are getting on — sleep, mood, diet, exercise</label>
              <textarea rows={3} value={generalWellbeing} onChange={e => setGeneralWellbeing(e.target.value)} placeholder="Patient-reported general wellbeing..." className={inputClass} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Today's weight (kg)</label>
                <input type="text" value={weightKg} onChange={e => setWeightKg(e.target.value)} placeholder="e.g. 94.2" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>BMI</label>
                <input type="text" value={bmi} onChange={e => setBmi(e.target.value)} placeholder="e.g. 31.4" className={inputClass} />
              </div>
            </div>

            <div>
              <label className={labelClass}>Weight change since last review / since baseline</label>
              <input type="text" value={weightChangeSinceLastReview} onChange={e => setWeightChangeSinceLastReview(e.target.value)} placeholder="e.g. −1.8 kg since last review / −3.6 kg since baseline" className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>Lifestyle changes — diet & physical activity</label>
              <textarea rows={2} value={lifestyleNotes} onChange={e => setLifestyleNotes(e.target.value)} placeholder="Diet and exercise changes reported..." className={inputClass} />
            </div>

            <YesNoRow label="Compliance concerns (administration; correct day/time)?" value={complianceConcerns} onChange={setComplianceConcerns} condLabel="Details:" condValue={complianceDetail} onCondChange={setComplianceDetail} isDarkMode={dm} />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 3 — Independent verification */}
      <Collapsible open={open[3]} onOpenChange={() => toggle(3)} className={cardClass}>
        <SectionHeader num={3} title="Independent verification" open={!!open[3]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>Full-length front &amp; side body photos received and reviewed (non-loose clothing)?</span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="photoFullLength" value={opt} checked={photoFullLength === opt} onChange={() => setPhotoFullLength(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {photoFullLength === "No" && (
                <div className="ml-4">
                  <label className={labelClass}>Action / reason:</label>
                  <TextInput value={photoFullLengthNo} onChange={setPhotoFullLengthNo} isDarkMode={dm} />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>Weighing-scale photo received and consistent with the inputted weight?</span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="photoScale" value={opt} checked={photoScale === opt} onChange={() => setPhotoScale(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {photoScale === "No" && (
                <div className="ml-4">
                  <label className={labelClass}>Action / reason:</label>
                  <TextInput value={photoScaleNo} onChange={setPhotoScaleNo} isDarkMode={dm} />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>Weight plausible against the expected trajectory?</span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="weightPlausible" value={opt} checked={weightPlausible === opt} onChange={() => setWeightPlausible(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {weightPlausible === "No" && (
                <div className="ml-4">
                  <label className={labelClass}>Query / action:</label>
                  <TextInput value={weightPlausibleDetail} onChange={setWeightPlausibleDetail} isDarkMode={dm} />
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 4 — Drug-specific checks */}
      <Collapsible open={open[4]} onOpenChange={() => toggle(4)} className={cardClass}>
        <SectionHeader num={4} title="Drug-specific checks" open={!!open[4]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div className="flex flex-wrap gap-3">
              {drugs.map(d => (
                <label key={d.key} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="drug-select" value={d.key} checked={selectedDrug === d.key} onChange={() => setSelectedDrug(d.key)} className="accent-blue-600" />
                  <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{d.label}</span>
                </label>
              ))}
            </div>

            {(selectedDrug === "tirzepatide" || selectedDrug === "semaglutide") && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className={labelClass}>Weekly injection — consistent injection day. Current → proposed next dose per titration schedule.</label>
                  <textarea rows={2} value={injectionNotes} onChange={e => setInjectionNotes(e.target.value)} placeholder="e.g. Injecting every Monday. Remaining on 5mg — not yet tolerating dose escalation." className={inputClass} />
                </div>
              </div>
            )}

            {selectedDrug === "liraglutide" && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Daily injection time</label>
                    <TextInput value={injectionTime} onChange={setInjectionTime} placeholder="e.g. 08:00 daily" isDarkMode={dm} />
                  </div>
                  <div>
                    <label className={labelClass}>Weekly titration step</label>
                    <TextInput value={titrationStep} onChange={setTitrationStep} placeholder="e.g. Week 4 → 0.6mg" isDarkMode={dm} />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>12-week efficacy review status</label>
                  <TextInput value={efficacy12wk} onChange={setEfficacy12wk} placeholder="e.g. Due at week 12 — in progress" isDarkMode={dm} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Brand name (mandatory — biological product)</label>
                    <select value={liraBrand} onChange={e => setLiraBrand(e.target.value)} className={inputClass}>
                      <option>Saxenda®</option>
                      <option>Biolide®</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Batch number (mandatory)</label>
                    <TextInput value={liraBatch} onChange={setLiraBatch} placeholder="Batch number" isDarkMode={dm} />
                  </div>
                </div>
              </div>
            )}

            {selectedDrug === "mysimba" && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className={labelClass}>Oral titration step</label>
                  <TextInput value={mysimbaStep} onChange={setMysimbaStep} placeholder="e.g. Week 3 — 2 morning + 1 evening" isDarkMode={dm} />
                </div>
                <div className={`p-3 rounded-lg border ${dm ? "bg-amber-900/20 border-amber-700" : "bg-amber-50 border-amber-200"}`}>
                  <p className={`text-xs font-semibold mb-2 ${dm ? "text-amber-300" : "text-amber-800"}`}>
                    BP & pulse after escalation (mandatory)
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={labelClass}>Systolic (mmHg)</label>
                      <TextInput value={bpSystolic} onChange={setBpSystolic} placeholder="e.g. 128" isDarkMode={dm} />
                    </div>
                    <div>
                      <label className={labelClass}>Diastolic (mmHg)</label>
                      <TextInput value={bpDiastolic} onChange={setBpDiastolic} placeholder="e.g. 82" isDarkMode={dm} />
                    </div>
                    <div>
                      <label className={labelClass}>Pulse (bpm)</label>
                      <TextInput value={pulse} onChange={setPulse} placeholder="e.g. 74" isDarkMode={dm} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isFoundayo && (
              <div className="mt-3">
                <AhtFoundayoReviewPanel
                  isDarkMode={dm}
                  initialWeightKg={planContext?.initialWeightKg ?? null}
                  lastWaistDate={planContext ? ((reviewRecords.latestValue(planContext.planId, "waistDate") as string | undefined) ?? null) : null}
                  priorMaxToleratedDoseDate={planContext ? ((reviewRecords.latestValue(planContext.planId, "maxToleratedDoseDate") as string | undefined) ?? null) : null}
                  onChange={setFoundayo}
                />
              </div>
            )}

            {selectedDrug === "orlistat" && (
              <div className="mt-3 space-y-3">
                <div className={`px-3 py-2 rounded-lg border text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-400" : "bg-gray-50 border-gray-200 text-gray-500"}`}>
                  Fixed dose (120mg TDS) — NO escalation
                </div>
                <div>
                  <label className={labelClass}>Fat-intake adherence notes</label>
                  <TextInput value={fatAdherenceNotes} onChange={setFatAdherenceNotes} placeholder="e.g. Patient maintaining &lt;30% calories from fat" isDarkMode={dm} />
                </div>
                <div>
                  <label className={labelClass}>12-week efficacy window status</label>
                  <TextInput value={orlistat12wk} onChange={setOrlistat12wk} placeholder="e.g. Week 10 — review due in 2 weeks" isDarkMode={dm} />
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 5 — Ongoing suitability / safety check */}
      <Collapsible open={open[5]} onOpenChange={() => toggle(5)} className={cardClass}>
        <SectionHeader num={5} title="Ongoing suitability / safety check" open={!!open[5]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>
                  Any new red flag, contraindication or exclusion now triggered? (e.g. pregnancy/planning, pancreatitis symptoms, gallbladder disease, significant mood/behaviour change)
                </span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="redFlagTriggered" value={opt} checked={redFlagTriggered === opt} onChange={() => setRedFlagTriggered(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {redFlagTriggered === "Yes" && (
                <div className="ml-4">
                  <label className={labelClass}>Stop / refer — detail:</label>
                  <TextInput value={redFlagDetail} onChange={setRedFlagDetail} isDarkMode={dm} className="border-red-500" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>
                  BMI floor reached? (tirzepatide/semaglutide — BMI falling below ~22–22.5; review continuation)
                </span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="bmiFloor" value={opt} checked={bmiFloor === opt} onChange={() => setBmiFloor(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {bmiFloor === "Yes" && (
                <div className="ml-4">
                  <label className={labelClass}>Action:</label>
                  <TextInput value={bmiFloorAction} onChange={setBmiFloorAction} isDarkMode={dm} className="border-red-500" />
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 6 — Prescribing decision & supply */}
      <Collapsible open={open[6]} onOpenChange={() => toggle(6)} className={cardClass}>
        <SectionHeader num={6} title="Prescribing decision & supply" open={!!open[6]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div>
              <label className={labelClass}>Decision</label>
              <div className="flex flex-wrap gap-4 mt-1">
                {["Continue same dose", "Escalate to next dose", "Hold", "Cease"].map(opt => (
                  <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="decision" value={opt} checked={decision === opt} onChange={() => setDecision(opt)} className="accent-blue-600" />
                    <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                  </label>
                ))}
              </div>
              {decision === "Cease" && (
                <div className={`mt-2 px-3 py-2 rounded-lg border text-sm ${dm ? "bg-amber-900/20 border-amber-700 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                  Document reason in clinical notes (Section 7)
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>Does the outcome match the patient's stated expectation?</span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="outcomeMatch" value={opt} checked={outcomeMatch === opt} onChange={() => setOutcomeMatch(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {outcomeMatch === "No" && (
                <div className={`px-3 py-2 rounded-lg border text-sm ${dm ? "bg-amber-900/20 border-amber-700 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                  Contact patient to book a prescriber appointment BEFORE proceeding
                </div>
              )}
            </div>

            <div>
              <label className={labelClass}>Medicine / dose prescribed</label>
              <TextInput value={medicinePrescribed} onChange={setMedicinePrescribed} placeholder="e.g. Mounjaro® 5mg" isDarkMode={dm} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Quantity</label>
                <TextInput value={quantity} onChange={setQuantity} placeholder="e.g. 4 pens" isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Batch no.</label>
                <TextInput value={supplyBatch} onChange={setSupplyBatch} placeholder="Batch" isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Expiry date</label>
                <TextInput value={supplyExpiry} onChange={setSupplyExpiry} placeholder="MM/YYYY" isDarkMode={dm} />
              </div>
            </div>

            <div>
              <label className={labelClass}>GP notified (with patient consent)?</label>
              <div className="flex gap-4 mt-1">
                {["Yes", "N/A"].map(opt => (
                  <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="gpNotified" value={opt} checked={gpNotified === opt} onChange={() => setGpNotified(opt)} className="accent-blue-600" />
                    <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className={labelClass}>Next review date</label>
              <input type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} className={inputClass} />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 7 — Clinical notes & rationale */}
      <Collapsible open={open[7]} onOpenChange={() => toggle(7)} className={cardClass}>
        <SectionHeader num={7} title="Clinical notes & rationale" open={!!open[7]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <textarea
              rows={6}
              value={clinicalNotes}
              onChange={e => setClinicalNotes(e.target.value)}
              placeholder="Document clinical reasoning, any concerns, and rationale for prescribing decision..."
              className={inputClass}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 8 — Prescriber sign-off */}
      <Collapsible open={open[8]} onOpenChange={() => toggle(8)} className={cardClass}>
        <SectionHeader num={8} title="Prescriber sign-off" open={!!open[8]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Prescriber name</label>
                <TextInput value={sigName} onChange={setSigName} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Registration no.</label>
                <TextInput value={sigRegNo} onChange={setSigRegNo} isDarkMode={dm} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Signature</label>
                <TextInput value={sigText} onChange={setSigText} placeholder="Type name as signature" isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Date</label>
                <input type="date" value={sigDate} onChange={e => setSigDate(e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Footer */}
      <p className={`text-center text-xs py-2 ${dm ? "text-gray-500" : "text-gray-400"}`}>
        Retain for 8 years · subject to internal and external audit · Allied Health Training Limited
      </p>

      {/* Submit */}
      {foundayoBlockers.length > 0 && (
        <ul
          className={`list-disc pl-6 text-xs ${dm ? "text-amber-300" : "text-amber-800"}`}
          data-testid="review-save-problems"
        >
          {foundayoBlockers.map(b => <li key={b}>{b}</li>)}
        </ul>
      )}
      <Button
        onClick={handleSubmit}
        disabled={loading || !effectiveDecision || !sigText || foundayoBlockers.length > 0}
        className="w-full mt-4"
      >
        {loading ? "Saving..." : "Complete 4-Week Review"}
      </Button>
    </div>
  );
}
